import { EVENT_LIMITS, type ScheduledEvent, type ScheduledEventInput, type ScheduledEventPayload, type ScheduledEventUpdate } from '@spawnwire/shared';

/** The rules and shapes behind the events screen, kept free of React so they can be tested on their own. */

const DAY = 86_400_000;

/** Soonest first; events starting at the same moment keep a steady order. */
export function sortEvents<T extends Pick<ScheduledEvent, 'startAt' | 'id'>>(list: T[]): T[] {
  return [...list].sort((a, b) => a.startAt.localeCompare(b.startAt) || a.id.localeCompare(b.id));
}

const isLive = (status: ScheduledEvent['status']) => status === 'scheduled' || status === 'active';

/**
 * Applies an event the server sent to the list of events still to come or running. Finished and canceled events
 * leave the list; this person's own `interested` stays, since events sent to everyone do not carry it.
 */
export function mergeEvent(list: ScheduledEvent[], incoming: ScheduledEventPayload): ScheduledEvent[] {
  const rest = list.filter((e) => e.id !== incoming.id);
  if (!isLive(incoming.status)) return rest;
  const before = list.find((e) => e.id === incoming.id);
  return sortEvents([...rest, { ...incoming, interested: before?.interested ?? false }]);
}

export function removeEvent(list: ScheduledEvent[], id: string): ScheduledEvent[] {
  return list.filter((e) => e.id !== id);
}

/** A new count of interested people for an event, from the server. Whether this person is interested is not touched. */
export function withInterestCount(list: ScheduledEvent[], id: string, interestedCount: number): ScheduledEvent[] {
  return list.map((e) => (e.id === id ? { ...e, interestedCount } : e));
}

/** The event as the server answered this person's own action, with whether they are interested. */
export function replaceEvent(list: ScheduledEvent[], event: ScheduledEvent): ScheduledEvent[] {
  return isLive(event.status) ? sortEvents([...list.filter((e) => e.id !== event.id), event]) : removeEvent(list, event.id);
}

// ---- The form -------------------------------------------------------------------------------

export interface EventForm {
  name: string;
  description: string;
  /** In a voice or stage channel, or somewhere else. */
  kind: 'channel' | 'external';
  channelId: string;
  location: string;
  /** Local date and time as a date-time input holds it: "2026-12-05T18:00". */
  start: string;
  end: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** An ISO time as the value of a date-time input, in the viewer's own time zone. */
export function toInputValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** What a date-time input holds, as an ISO time; null when it is empty or not a date. */
export function fromInputValue(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** A blank form: starting at the top of the next hour after one hour from now, so there is time to finish setting it up. */
export function emptyForm(channelId = '', now = Date.now()): EventForm {
  const start = new Date(now + 3_600_000);
  start.setMinutes(0, 0, 0);
  start.setHours(start.getHours() + 1);
  return { name: '', description: '', kind: channelId ? 'channel' : 'external', channelId, location: '', start: toInputValue(start.toISOString()), end: '' };
}

export function formFromEvent(e: ScheduledEvent): EventForm {
  return {
    name: e.name,
    description: e.description,
    kind: e.channelId ? 'channel' : 'external',
    channelId: e.channelId ?? '',
    location: e.location ?? '',
    start: toInputValue(e.startAt),
    end: e.endAt ? toInputValue(e.endAt) : '',
  };
}

/**
 * The first problem with the form, in words, or null when the server will accept it. Mirrors the server's rules so
 * mistakes show before sending; the server still checks everything. `running` is an event that has started: its
 * start can no longer change, so the start time is not held against it.
 */
export function validateForm(form: EventForm, now = Date.now(), running = false): string | null {
  const name = form.name.trim();
  if (!name) return 'Give the event a name.';
  if (name.length > EVENT_LIMITS.name) return `The name can be at most ${EVENT_LIMITS.name} characters.`;
  if (form.description.trim().length > EVENT_LIMITS.description) return `The description can be at most ${EVENT_LIMITS.description} characters.`;
  if (form.kind === 'channel' && !form.channelId) return 'Pick a voice or stage channel.';
  if (form.kind === 'external') {
    if (!form.location.trim()) return 'Say where it takes place.';
    if (form.location.trim().length > EVENT_LIMITS.location) return `The place can be at most ${EVENT_LIMITS.location} characters.`;
    if (!form.end) return 'An event outside the app needs an end time.';
  }
  const start = fromInputValue(form.start);
  const end = form.end ? fromInputValue(form.end) : null;
  if (!start) return 'Pick a start time.';
  if (form.end && !end) return 'The end time is not a valid date.';
  const startMs = Date.parse(start);
  if (!running) {
    if (startMs <= now) return 'The event has to start in the future.';
    if (startMs > now + EVENT_LIMITS.maxAheadDays * DAY) return `An event can be scheduled at most ${EVENT_LIMITS.maxAheadDays} days ahead.`;
  }
  if (end) {
    const endMs = Date.parse(end);
    if (endMs <= (running ? now : startMs)) return running ? 'The end has to be in the future.' : 'The event has to end after it starts.';
    if (endMs - startMs > EVENT_LIMITS.maxLengthDays * DAY) return `An event can last at most ${EVENT_LIMITS.maxLengthDays} days.`;
  }
  return null;
}

/** What to send to schedule the event. */
export function formToCreate(form: EventForm): ScheduledEventInput {
  const end = form.end ? fromInputValue(form.end) : null;
  return {
    name: form.name.trim(),
    description: form.description.trim(),
    ...(form.kind === 'channel' ? { channelId: form.channelId } : { location: form.location.trim() }),
    startAt: fromInputValue(form.start)!,
    ...(end ? { endAt: end } : {}),
  };
}

/** Only what changed from the event, so an edit never overwrites something it did not touch. Null when nothing changed. */
export function formToPatch(form: EventForm, event: ScheduledEvent): ScheduledEventUpdate | null {
  const patch: ScheduledEventUpdate = {};
  if (form.name.trim() !== event.name) patch.name = form.name.trim();
  if (form.description.trim() !== event.description) patch.description = form.description.trim();
  if (form.kind === 'channel') {
    if (form.channelId !== event.channelId) patch.channelId = form.channelId;
  } else if (form.location.trim() !== (event.location ?? '') || event.channelId) {
    patch.location = form.location.trim();
  }
  const start = fromInputValue(form.start);
  if (start && toInputValue(start) !== toInputValue(event.startAt)) patch.startAt = start;
  const end = form.end ? fromInputValue(form.end) : null;
  if ((end ? toInputValue(end) : '') !== (event.endAt ? toInputValue(event.endAt) : '')) patch.endAt = end;
  return Object.keys(patch).length ? patch : null;
}

// ---- Words ----------------------------------------------------------------------------------

/** "in 5 minutes", "in 3 hours", "in 2 days"; "now" once it is due. */
export function countdown(iso: string, now = Date.now()): string {
  const ms = Date.parse(iso) - now;
  if (ms <= 60_000) return 'now';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `in ${days} days`;
}

/** "1 person is interested", "12 people are interested", "No one yet". */
export function interestText(count: number): string {
  if (count <= 0) return 'No one is interested yet';
  return count === 1 ? '1 person is interested' : `${count} people are interested`;
}
